import React, {FC, useMemo} from 'react';
import {entries, LocaleType} from 'src/types/general';
import {Typography} from '@components/Typography';
import {FormattedDate, FormattedMessage, FormattedNumber} from 'react-intl';
import {ParamsTable} from '@components/ParamsTable';
import {Section} from '@components/Section';
import {DateTime} from 'luxon';
import {
    localizedListSlugs as dispositionListSlugs,
    translations as dispositionTranslations
} from '@constants/Disposition';
import {translations as equippedTranslations} from '@constants/Equipped';
import {translations as conditionTranslations} from '@constants/Condition';
import {translations as ownershipTranslations} from '@constants/Ownership';
import {translations as constructionTranslations} from '@constants/Construction';
import {translations as landTypeTranslations} from '@constants/LandType';
import {translations as penbTranslations} from '@constants/Penb';
import {translations as ageTranslations} from '@constants/Age';
import {translations as heatingTranslations} from '@constants/Heating';
import {translations as reconstructionTranslations} from '@constants/Reconstruction';
import {translations as executionTranslations} from '@constants/Execution';
import {translations as waterTranslations} from '@constants/Water';
import {translations as sewageTranslations} from '@constants/Sewage';
import {Currency, Disposition, EstateType, Locale, OfferType} from '.cache/__types__';
import Link from "next/link";
import {getLocalizedRoute, routes} from "@utils/routes";
import {localizedListSlugs as offerListSlugs} from "@constants/OfferType";
import {localizedListSlugs as estateListSlugs} from "@constants/EstateType";
import {useLocale} from "@utils/locale";

// @ts-ignore
const basicParameters: Record<
    | 'id'
    | 'surface'
    | 'disposition'
    | 'equipped'
    | 'availableFrom'
    | 'surfaceLand'
    | 'condition'
    | 'floor'
    | 'ownership'
    | 'construction'
    | 'landType'
    | 'penb'
    | 'age'
    | 'water'
    | 'sewage'
    | 'heating'
    | 'reconstruction'
    | 'execution'
    | 'fee',
    | {
          type: 'string' | 'date' | 'number' | 'currency';
          label: React.ReactElement;
          append?: string;
      }
    | {type: 'constant'; translations: Record<string, any>; label: React.ReactElement; append?: string}
    | {type: 'function'; label: React.ReactElement; append?: string; value: (advert: BasicParametersProps["advert"], locale: LocaleType) => React.ReactElement;}
> = {
    id: {type: 'string', label: <FormattedMessage defaultMessage={'Číslo inzerátu'} />},
    surface: {type: 'number', label: <FormattedMessage defaultMessage={'Plocha'} />, append: ' m²'},
    disposition: {
        type: 'function',
        label: <FormattedMessage defaultMessage={'Dispozice'} />,
        value: (advert, locale) => (
            <Link href={getLocalizedRoute(routes.list, locale, {list: [
                offerListSlugs[locale][advert.offerType === OfferType.Undefined ? OfferType.Prodej : advert.offerType],
                    estateListSlugs[locale][advert.estateType === EstateType.Undefined ? EstateType.Byt : advert.estateType],
                    dispositionListSlugs[locale][(advert.disposition === Disposition.Undefined ? Disposition.Ostatni : advert.disposition) as Exclude<Disposition, Disposition.Undefined>]].join('/')})}>
                <a>
                    {dispositionTranslations[advert.disposition as Disposition]}
                </a>
            </Link>
        )
    },
    equipped: {
        type: 'constant',
        translations: equippedTranslations,
        label: <FormattedMessage defaultMessage={'Vybaveno'} />,
    },
    availableFrom: {type: 'date', label: <FormattedMessage defaultMessage={'Dostupné od'} />},
    surfaceLand: {type: 'number', label: <FormattedMessage defaultMessage={'Plocha pozemku'} />, append: ' m²'},
    condition: {
        type: 'constant',
        translations: conditionTranslations,
        label: <FormattedMessage defaultMessage={'Stav'} />,
    },
    floor: {type: 'number', label: <FormattedMessage defaultMessage={'Podlaží'} />},
    ownership: {
        type: 'constant',
        translations: ownershipTranslations,
        label: <FormattedMessage defaultMessage={'Vlastnictví'} />,
    },
    construction: {
        type: 'constant',
        translations: constructionTranslations,
        label: <FormattedMessage defaultMessage={'Typ budovy'} />,
    },
    landType: {
        type: 'constant',
        translations: landTypeTranslations,
        label: <FormattedMessage defaultMessage={'Typ pozemku'} />,
    },
    penb: {type: 'constant', translations: penbTranslations, label: <FormattedMessage defaultMessage={'PENB'} />},
    age: {type: 'constant', translations: ageTranslations, label: <FormattedMessage defaultMessage={'Stáří'} />},
    heating: {
        type: 'constant',
        translations: heatingTranslations,
        label: <FormattedMessage defaultMessage={'Vytápění'} />,
    },
    reconstruction: {
        type: 'constant',
        translations: reconstructionTranslations,
        label: <FormattedMessage defaultMessage={'Rekonstrukce'} />,
    },
    execution: {
        type: 'constant',
        translations: executionTranslations,
        label: <FormattedMessage defaultMessage={'Provedení'} />,
    },
    water: {type: 'constant', translations: waterTranslations, label: <FormattedMessage defaultMessage={'Voda'} />},
    sewage: {type: 'constant', translations: sewageTranslations, label: <FormattedMessage defaultMessage={'Odpad'} />},
    fee: {type: 'currency', label: <FormattedMessage defaultMessage={'Administrativní poplatky'} />},
};

type BasicParameterProps = {
    advert: BasicParametersProps["advert"];
    label: React.ReactElement;
    transform?: ((advert: BasicParametersProps["advert"], locale: LocaleType) => React.ReactElement);
    type: string;
    append?: string;
    unit?: string;
    value: any;
    currency: Currency;
};

const BasicParameter: FC<BasicParameterProps> = ({advert, label, value, append, type, unit, currency, transform}) => {
    const locale = useLocale();
    return (
        <tr>
            <th>{label}</th>
            <td>
                {type === 'number' && typeof value === 'number' && (
                    <FormattedNumber value={value} style={unit ? 'unit' : undefined} unit={unit} />
                )}

                {type === 'currency' && typeof value === 'number' && (
                    <FormattedNumber value={value} style={'currency'} currency={currency} maximumFractionDigits={0} />
                )}

                {type === 'string' && value}

                {type === 'date' && <FormattedDate value={DateTime.fromSeconds(value).toJSDate()} />}

                {type === 'constant' && value}

                {type === 'function' && typeof transform === "function" && transform(advert, locale)}

                {append}
            </td>
        </tr>
    );
};

export type BasicParametersProps = {
    advert: Partial<Record<keyof typeof basicParameters, any>> & {currency: Currency, disposition: Disposition, locale: Locale, offerType: OfferType, estateType: EstateType};
};

export const BasicParameters: FC<BasicParametersProps> = ({advert}) => {
    const availableBasicParameters = useMemo(() => {
        return entries(basicParameters).filter(([key, _]) => advert.hasOwnProperty(key) && advert[key] && advert[key] !== 'UNDEFINED');
    }, [advert]);

    return (
        <Section className="mb-5 mb-lg-10">
            <Typography as="h2" className="mb-3 mb-lg-8" variant="subheadline">
                <FormattedMessage defaultMessage={'Parametry nemovitosti'} />
            </Typography>
            <ParamsTable>
                <ParamsTable.Group>
                    <table>
                        <tbody>
                            {availableBasicParameters
                                .filter((_, idx) => idx % 2 === 0)
                                .map(([key, parameter]) => (
                                    <BasicParameter
                                        advert={advert}
                                        label={parameter.label}
                                        type={parameter.type}
                                        value={parameter.type === 'constant' ? parameter.translations[advert[key]] : advert[key]}
                                        currency={advert.currency}
                                        append={parameter.append}
                                        transform={parameter.type === "function" ? parameter.value : undefined}
                                        key={key}
                                    />
                                ))}
                        </tbody>
                    </table>
                </ParamsTable.Group>
                <ParamsTable.Group>
                    <table>
                        <tbody>
                            {availableBasicParameters
                                .filter((_, idx) => idx % 2 === 1)
                                .map(([key, parameter]) => (
                                    <BasicParameter
                                        advert={advert}
                                        label={parameter.label}
                                        type={parameter.type}
                                        value={parameter.type === 'constant' ? parameter.translations[advert[key]] : advert[key]}
                                        append={parameter.append}
                                        currency={advert.currency}
                                        transform={parameter.type === "function" ? parameter.value : undefined}
                                        key={key}
                                    />
                                ))}
                        </tbody>
                    </table>
                </ParamsTable.Group>
            </ParamsTable>
        </Section>
    );
};
